import { HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { DialogService } from "./dialog/dialog-service";
import { lastValueFrom, Observable } from "rxjs";
import { environment } from "../../environments/environment";
import { LoadingService } from "../../services/loading.service";
import { finalize } from 'rxjs/operators';
@Injectable({
  providedIn: 'root'
})
export class HttpService {
  public gridData: any[] = [];

  constructor(readonly http: HttpClient, readonly dialogService: DialogService, readonly spinner: LoadingService) {

  }

  getFile(uri: string, fileName = 'download', onComplete: (Function|null) = null) {
    const opId = this.spinner.show(this._loadingMessage(uri, 'FILE'), 8000);

    this.http.get(FormatURI(uri), {
      responseType: 'blob',
      observe: 'response'
    }).pipe(
      finalize(() => this.spinner.hide(opId))
    ).subscribe({
      next: (res: HttpResponse<Blob>) => {
        const blob = res.body;
        if (!blob) {
          this.dialogService.showError('Download failed: empty response.');
          return;
        }

        const fn =
          this.tryGetFileNameFromContentDisposition(res) ??
          fileName;
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = fn;
        a.click();
        URL.revokeObjectURL(objectUrl);

        if (onComplete) onComplete(true);
      },
      error: async (err: HttpErrorResponse) => {
        const message = await this.extractErrorMessage(err)
          ?? 'Unable to process download, please contact WellChron support';
        this.dialogService.showError(message);
        if (onComplete) onComplete(false);
      }
    });
  }

  private tryGetFileNameFromContentDisposition(res: HttpResponse<Blob>): string | null {
    const cd = res.headers.get('Content-Disposition') || res.headers.get('content-disposition');
    if (!cd) return null;

    const fnStar = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(cd);
    if (fnStar?.[1]) return decodeURIComponent(fnStar[1].replace(/["']/g, ''));

    const fn = /filename\s*=\s*("?)([^";]+)\1/i.exec(cd);
    if (fn?.[2]) return fn[2];

    return null;
  }

  private async extractErrorMessage(err: HttpErrorResponse): Promise<string | null> {
    const e = err.error;

    if (e instanceof Blob) {
      try {
        const text = await e.text();
        if (!text?.trim()) return null;

        try {
          const json = JSON.parse(text);
          return json?.message ?? json?.error ?? json?.title ?? null;
        } catch {
          return text.trim();
        }
      } catch {
        return null;
      }
    }

    if (typeof e === 'object' && e) {
      return (e as any).message ?? (e as any).error ?? (e as any).title ?? null;
    }

    if (typeof e === 'string' && e.trim()) return e.trim();

    return err.message ?? null;
  }

  postBlob(uri: string, onSuccess: (blob: Blob) => void, data?: any, body?: any) {
    const opId = this.spinner.show(this._loadingMessage(uri, 'FILE'), 8000);
    this.http.post(FormatURI(uri), body ?? {}, {
      params: data,
      responseType: 'blob' as 'json'
    }).subscribe({
      next: (b: any) => {
        this.spinner.hide(opId);
        onSuccess(b as Blob);
      },
      error: (_: any) => {
        this.spinner.hide(opId);
        this.dialogService.showError('Unknown error occured, please contact Well Chron support');
      },
      complete: () => this.spinner.hide(opId)
    });
  }

  postReturn<T>(uri: string, onSuccess: Function, data?: any, body?: any, onError: (Function|undefined) = undefined) {
    const opId = this.spinner.show(this._loadingMessage(uri, 'POST'));
    this.http.post<any>(FormatURI(uri), body || {}, { params: data }).subscribe({
      next: (x: any) => {
        try {
          this.spinner.hide(opId);
          if (x && 'errorIndicator' in x) {
            this.dialogService.showError(x.message);
            if (onError) onError();
          } else {
            onSuccess(x)
          }
        }
        catch (e: any) {
          this.dialogService.showError('Unknown error occured, please contact Well Chron support');
          if (onError) onError();
        }
      }, error: (x: any) => {
        this.spinner.hide(opId);
        const msg = x?.error?.message?.trim();
        this.dialogService.showError(msg || 'Unknown error occured, please contact Well Chron support');
        if (onError) onError();
      }, complete: () => {
        this.spinner.hide(opId);
      }
    })
  }

  post(uri: string, onSuccess: Function, data?: any, body?: any) {
    this.postReturn<any>(uri, onSuccess, data, body);
  }

  putReturn<T>(uri: string, onSuccess: Function, data?: any, body?: any, onError: (Function|undefined) = undefined) {
    const opId = this.spinner.show(this._loadingMessage(uri, 'POST'));
    this.http.put<any>(FormatURI(uri), body || {}, { params: data }).subscribe({
      next: (x: any) => {
        try {
          this.spinner.hide(opId);
          if (x && 'errorIndicator' in x) {
            this.dialogService.showError(x.message);
            if (onError) onError();
          } else {
            onSuccess(x)
          }
        }
        catch (e: any) {
          this.dialogService.showError('Unknown error occured, please contact Well Chron support');
          if (onError) onError();
        }
      }, error: (x: any) => {
        this.spinner.hide(opId);
        const msg = x?.error?.message?.trim();
        this.dialogService.showError(msg || 'Unknown error occured, please contact Well Chron support');
        if (onError) onError();
      }, complete: () => {
        this.spinner.hide(opId);
      }
    })
  }

  put(uri: string, onSuccess: Function, data?: any, body?: any) {
    this.putReturn<any>(uri, onSuccess, data, body);
  }

  deleteReturn<T>(
    uri: string,
    onSuccess: Function,
    onError: (Function | undefined) = undefined
  ) {
    const opId = this.spinner.show(this._loadingMessage(uri, 'POST'));

    this.http.delete<any>(FormatURI(uri)).subscribe({
      next: (x: any) => {
        try {
          this.spinner.hide(opId);

          if (x && 'errorIndicator' in x) {
            this.dialogService.showError(x.message);
            if (onError) onError();
          } else {
            onSuccess(x);
          }
        } catch (e: any) {
          this.dialogService.showError('Unknown error occured, please contact Well Chron support');
          if (onError) onError();
        }
      },
      error: (x: any) => {
        this.spinner.hide(opId);
        const msg = x?.error?.message?.trim();
        this.dialogService.showError(msg || 'Unknown error occured, please contact Well Chron support');
        if (onError) onError();
      },
      complete: () => {
        this.spinner.hide(opId);
      }
    });
  }

  async getWait<T>(uri: string, isJson = true, spinner = true) {
    const opId = spinner ? this.spinner.show(this._loadingMessage(uri, 'GET')) : undefined;
    try {
      var o = isJson ? this.http.get<any>(FormatURI(uri)) : this.http.get<any>(FormatURI(uri), { responseType: 'text' as 'json' });
      var x = await lastValueFrom(o);
      if (x && x['errorIndicator']) {
        var internalUri = uri;
        this.dialogService.showError(x.message);
        return undefined;
      } else {
        var testRes = x as T;
        return testRes;
      }
    } finally {
      // A rejected request (network error, 401, etc.) must still release this
      // operation -- without this, a thrown/rejected call here left its loading
      // overlay registered forever, since nothing else ever calls hide() for it.
      if (opId) this.spinner.hide(opId);
    }
  }

  get<T>(uri: string, onSuccess: Function, showSpinner:boolean = true) {
    const opId = showSpinner ? this.spinner.show(this._loadingMessage(uri, 'GET')) : undefined;
    this.http.get<any>(FormatURI(uri)).subscribe({
      next: (x: any) => {
        if (opId) this.spinner.hide(opId);
        if (x != null) {
          if (x['errorIndicator']) {
            this.dialogService.showError(x.message);
          } else {
            onSuccess(x as T)
          }
        }
      }, error: (x: any) => {
        var innerUri = uri;
        if (opId) this.spinner.hide(opId);
        this.dialogService.showError('Unknown error occured, please contact Well Chron support');
      }, complete: () => {
        if (opId) this.spinner.hide(opId);
      }
    })
  }

  /** Map URI patterns to user-friendly status messages */
  private _loadingMessage(uri: string, verb: 'GET' | 'POST' | 'FILE'): string {
    const lower = uri.toLowerCase();
    if (verb === 'FILE') return 'Downloading file\u2026';
    if (lower.includes('/logout')) return 'Signing out\u2026';
    if (lower.includes('grid/') && lower.includes('/definition')) return 'Loading grid layout\u2026';
    if (lower.includes('grid/') && lower.includes('/data')) return 'Querying data\u2026';
    if (lower.includes('grid/') && lower.includes('/bulkops')) return 'Processing changes\u2026';
    if (lower.includes('grid/') && lower.includes('/lookups')) return 'Loading options\u2026';
    if (lower.includes('/savedata')) return 'Saving records\u2026';
    if (lower.includes('/export')) return 'Exporting data\u2026';
    if (lower.includes('user')) return 'Loading users\u2026';
    if (lower.includes('report')) return 'Generating report\u2026';
    if (lower.includes('schedule')) return 'Loading schedules\u2026';
    if (lower.includes('dashboard')) return 'Loading dashboard\u2026';
    if (lower.includes('attachment')) return 'Loading attachments\u2026';
    if (lower.includes('calc')) return 'Running Calculations\u2026';
    if (verb === 'POST') return 'Saving\u2026';
    return 'Loading\u2026';
  }

}


export function FormatURI(relativePath: string): string {
  return environment.apiConfig.uri + relativePath;
}
